import { canvasSearch, inspectorSearch } from '../workspace/-search'
import type { WorkspaceSearch } from '../workspace/-search'

function positiveSearchID(value: unknown): number | undefined {
  if (Array.isArray(value)) return undefined
  const number =
    typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
  return Number.isSafeInteger(number) && number > 0 ? number : undefined
}

// The chat routes are now redirects, and this is the only thing left that understands
// the shape they used to carry: `panel=` and its per-kind identifiers. It maps a link
// written before the split onto the workspace URL that replaces it, so a bookmark, a
// copied link or a URL written into the project notes keeps resolving. A `panel` that
// never resolved to anything drops, leaving the chat to open on its own. See ADR-167.
export function legacyWorkspaceSearch(raw: unknown, chat?: number): WorkspaceSearch {
  const source = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>
  const base = chat === undefined ? {} : { chat }
  const panel = source['panel']

  if (panel === 'facts') {
    const briefId = positiveSearchID(source['briefId'])
    return briefId === undefined ? base : { ...base, ...inspectorSearch({ inspector: 'facts', briefId }) }
  }
  if (panel === 'sources') {
    const messageId = positiveSearchID(source['messageId'])
    return messageId === undefined ? base : { ...base, ...inspectorSearch({ inspector: 'sources', messageId }) }
  }
  if (panel === 'proposal') {
    const proposalId = positiveSearchID(source['proposalId'])
    const sectionId = positiveSearchID(source['sectionId'])
    return proposalId === undefined || sectionId === undefined
      ? base
      : { ...base, ...canvasSearch({ canvas: 'proposal', proposalId, sectionId }) }
  }
  if (panel === 'presentation') {
    const presentationId = positiveSearchID(source['presentationId'])
    return presentationId === undefined
      ? base
      : { ...base, ...canvasSearch({ canvas: 'presentation', presentationId }) }
  }
  if (panel === 'slide') {
    const presentationId = positiveSearchID(source['presentationId'])
    const slideId = positiveSearchID(source['slideId'])
    // A legacy slide link carries versionId too. It is read only to be discarded: the new
    // address names the slide, so an old link opens it rather than dead-ending on a version
    // that has since been superseded.
    return presentationId === undefined || slideId === undefined
      ? base
      : { ...base, ...canvasSearch({ canvas: 'presentation', presentationId, slideId }) }
  }
  if (panel === 'collection') {
    const collection = source['collection']
    return collection === 'proposals' ||
      collection === 'presentations' ||
      collection === 'content' ||
      collection === 'connectors'
      ? { ...base, ...canvasSearch({ canvas: 'collection', collection }) }
      : base
  }
  return base
}
